import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DialogService } from '@services/dialog.service';
import { UserService } from '@services/user.service';
import { ChitinButtonComponent } from '@shared/components/chitin/atoms/button/button.component';
import { View } from '@shared/components/data-view/data-view-types';
import { DataViewEditColumnsComponent } from '@shared/components/data-view/edit-columns/data-view-edit-columns.component';
import { DataViewSaveViewComponent } from '@shared/components/data-view/save-view/data-view-save-view.component';
import { DataViewPageStore } from '@shared/dataview/state/page.store';
import { isEqual } from 'lodash';

@Component({
  selector: 'dv-view-actions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChitinButtonComponent],
  host: { style: 'display: contents' },
  template: `
    @if (page.views.hasUnsavedChanges()) {
      <chitin-button
        size="small"
        label="Save view"
        class="text-nowrap"
        data-testid="dv-save-view"
        [disabled]="!page.canSaveViews()"
        (clicked)="saveView()"
      />
      <chitin-button
        size="small"
        color="secondary"
        label="Reset view"
        class="text-nowrap"
        data-testid="dv-reset-view"
        (clicked)="page.discardChanges()"
      />
    }
    <chitin-button
      size="small"
      color="secondary"
      label="Edit columns"
      class="text-nowrap"
      data-testid="dv-edit-columns"
      (clicked)="editColumns()"
    />
  `,
})
export class DvViewActionsComponent {
  private readonly dialogService = inject(DialogService);
  protected readonly page = inject<DataViewPageStore<unknown>>(DataViewPageStore);
  private readonly userService = inject(UserService);

  protected saveView(): void {
    const view = this.page.views.current();
    if (!view) return;

    const modal = this.dialogService.openModal(DataViewSaveViewComponent, { centered: true });
    const saveView = modal.componentInstance;
    saveView.editedView = { id: view.id, label: view.label } as View.UserView;
    saveView.currentUser = this.userService.getCurrentUser();
    saveView.existingLabels = this.page.views.views().map(existing => existing.label);
    saveView.isViewEditable = this.page.canSaveCurrent();

    modal.closed.subscribe(({ mode, label }: { mode: 'update' | 'create'; label: string }) => {
      if (mode === 'update') this.page.saveCurrentView(label);
      else this.page.saveViewAs({ id: crypto.randomUUID(), label });
    });
  }

  protected editColumns(): void {
    const columns = this.page.columns;
    const favouriteIds = columns.favouriteIds();

    const slideout = this.dialogService.openSlideout(DataViewEditColumnsComponent, { position: 'end', panelClass: 'editColumnsPanel' });
    const gallery = slideout.componentInstance;
    gallery.appliedColumns.set(columns.gridColumns().map(column => ({ ...column })));
    gallery.favouriteColumnIds.set([...favouriteIds]);
    gallery.setColumns(
      this.page.categories(),
      columns.available().map(column => ({ ...column })),
    );

    gallery.triggerColumnsUpdate.subscribe(() => {
      columns.applyFromSchema(gallery.appliedColumns().map(({ id, width }) => ({ id, width })));
      columns.favouriteIds.set([...gallery.favouriteColumnIds()]);
      gallery.appliedColumns.set([...gallery.appliedColumns()]);
    });

    slideout.hidden.subscribe(() => {
      if (!isEqual(favouriteIds, columns.favouriteIds())) this.page.savePreferences();
    });
  }
}
